import { Vue } from 'vue-property-decorator';
import axios from 'axios';
import { nextSpecialDates, parseKinds } from '../SpecialDays/specialDayCalendar';

const API = process.env.VUE_APP_API_ADMIN_LOCATION + 'notification/';

// Same keys as cloud-function-go usecases/segments/RULE_KEYS.md; admin-api validates the value.
export const SEGMENT_RULE_KEYS = [
  { key: 'all_users', value: 'ignored, use 0 (every user except therapists)' },
  { key: 'no_of_appointment', value: 'min,max completed appointments, min <= max' },
  { key: 'register_since_days', value: 'from,to rolling days ago, from > to, e.g. 14,0' },
  { key: 'no_of_pending_appointment', value: 'min,max future pending appointments, min <= max' },
  { key: 'earliest_pending_appointment_within_next_days', value: 'N days' },
  { key: 'done_test_within_days', value: 'from,to rolling days ago, from > to, e.g. 30,0' },
  { key: 'last_appointment_within_days', value: 'from,to rolling days ago, from > to, e.g. 30,0' },
  { key: 'last_active_within_days', value: 'N days (N,M also accepted, M is ignored)' },
  { key: 'registered_days_ago', value: 'N, one Bangkok calendar day N days before today' },
  { key: 'completed_appointment_days_ago', value: 'N, one Bangkok calendar day N days before today' },
  { key: 'first_booking_days_ago', value: 'N, one Bangkok calendar day N days before today' },
  { key: 'patient_cancelled_days_ago', value: 'N, one Bangkok calendar day N days before today' },
  { key: 'low_review_months_ago', value: 'N months, at least 1 (the Bangkok day N months before today)' },
  { key: 'registered_months_ago', value: 'N months, at least 1 (6 = half-year, 12 = 1-year anniversary)' },
  { key: 'pulse_not_checked_in_today', value: 'N days, at least 1: checked in within the last N days, not today' },
  { key: 'discount_package_ends_in_days', value: 'N, one Bangkok calendar day N days from today (0 = today)' },
  { key: 'b2b_entitlement_ends_in_days', value: 'N, one Bangkok calendar day N days from today (0 = today)' },
  { key: 'birthday_today', value: 'ignored, use 0' },
  {
    key: 'completed_appointment_topic_ids',
    value: 'topic id,id,... each at least 1; no time window, child topics not followed',
  },
  { key: 'user_ids', value: 'id,id,...' },
  { key: 'kbank_zscore_anomaly', value: 'z, one decimal number e.g. -0.67' },
  { key: 'ooca_employee', value: 'ignored, use 0' },
  { key: 'nhso_no_done_pre_test', value: 'ignored, use 0' },
  { key: 'has_pending_checkup', value: 'ignored, use 0' },
];

const NOTIFICATION_TIERS = [
  { value: 'T0', text: 'T0 essential' },
  { value: 'TC', text: 'TC special day' },
  { value: 'T1', text: 'T1 personal' },
  { value: 'T2', text: 'T2 behaviour group' },
  { value: 'T3', text: 'T3 general' },
];

const toInteger = (value) => (value === '' || value === null ? null : Number(value));

export default Vue.extend({
  data: () => ({
    segmentItems: [],
    selectedSegmentId: null,
    dialogError: '',
    isShowSnackbar: false,
    snackbarWarnings: [],
    isEditMessageOpen: false,
    editMessageDialogTitle: '',
    addNotificationId: '',
    editMessageId: '',
    editMessageTitle: '',
    editMessageBody: '',
    editMessageTitleEn: '',
    editMessageBodyEn: '',
    editMessageUrl: '',
    isNotificationRuleOpen: false,
    notificationRuleId: null,
    notificationRuleCron: '',
    notificationRuleActive: false,
    notificationRuleTier: 'T3',
    notificationRulePriority: 0,
    notificationRuleCooldownDays: null,
    notificationRuleAllowQuietHours: false,
    notificationRuleSpecialDayKinds: [],
    notificationTiers: NOTIFICATION_TIERS,
    isSegmentRuleOpen: false,
    segmentRuleId: null,
    segmentRuleKey: '',
    segmentRuleValue: '',
    segmentRuleNegate: false,
    segmentRuleKeys: SEGMENT_RULE_KEYS.map(({ key }) => key),
    isSegmentOpen: false,
    segmentName: '',
    segmentDescription: '',
    specialDayKinds: [],
  }),
  methods: {
    refresh() {
      axios.get(API + 'segments').then(({ data }) => {
        this.segmentItems = data.segments.map((item) => ({
          text: item.active ? item.name : `${item.name} (inactive)`,
          value: item.id,
          data: item,
        }));

        if (this.segmentItems.length > 0 && this.selectedSegmentId === null) {
          this.selectedSegmentId = this.segmentItems[0].value;
        }
      });
      axios.get(API + 'special-day-kinds').then(({ data }) => {
        this.specialDayKinds = data.special_day_kinds;
      });
    },
    showSnackbar(lines) {
      this.snackbarWarnings = lines;
      this.isShowSnackbar = true;
    },
    errorMessage(err) {
      const { response } = err;
      return (response && response.data.error && response.data.error.message) || err.message;
    },
    async confirmThen(title, text, request) {
      const confirmed = await this.$root.$confirm(title, text, { color: 'primary', submitText: title });
      if (!confirmed) {
        return;
      }
      try {
        const { data } = await request();
        if (data.warnings && data.warnings.length > 0) {
          this.showSnackbar(data.warnings);
        }
        this.refresh();
      } catch (err) {
        this.showSnackbar([this.errorMessage(err)]);
      }
    },
    async submit(request, closeDialog) {
      this.dialogError = '';
      try {
        const { data } = await request();
        closeDialog();
        if (data.warnings && data.warnings.length > 0) {
          this.showSnackbar(data.warnings);
        }
        this.refresh();
      } catch (err) {
        this.dialogError = this.errorMessage(err);
      }
    },
    addMessage(notificationId) {
      this.editMessageDialogTitle = 'Add Message';
      this.addNotificationId = notificationId;
      this.editMessageId = null;
      this.editMessageTitle = '';
      this.editMessageBody = '';
      this.editMessageTitleEn = '';
      this.editMessageBodyEn = '';
      this.editMessageUrl = '';
      this.dialogError = '';
      this.isEditMessageOpen = true;
    },
    editMessage(message) {
      this.editMessageDialogTitle = 'Edit Message';
      this.editMessageId = message.id;
      this.editMessageTitle = message.title.th;
      this.editMessageBody = message.body.th;
      this.editMessageTitleEn = message.title.en || '';
      this.editMessageBodyEn = message.body.en || '';
      this.editMessageUrl = message.url;
      this.dialogError = '';
      this.isEditMessageOpen = true;
    },
    saveMessage() {
      const payload = {
        title: this.editMessageTitle,
        body: this.editMessageBody,
        title_en: this.editMessageTitleEn,
        body_en: this.editMessageBodyEn,
        url: this.editMessageUrl,
      };
      const request =
        this.editMessageId === null
          ? () => axios.post(API + `message/${this.addNotificationId}`, payload)
          : () => axios.patch(API + `message/${this.editMessageId}`, payload);
      return this.submit(request, () => (this.isEditMessageOpen = false));
    },
    deleteMessage(messageId) {
      return this.confirmThen('Delete', 'Do you want to delete this message?', () =>
        axios.delete(API + `message/${messageId}`)
      );
    },
    addSegment() {
      this.segmentName = '';
      this.segmentDescription = '';
      this.dialogError = '';
      this.isSegmentOpen = true;
    },
    saveSegment() {
      const request = async () => {
        const response = await axios.post(API + 'segments', {
          name: this.segmentName,
          description: this.segmentDescription,
        });
        this.selectedSegmentId = response.data.id;
        return response;
      };
      return this.submit(request, () => (this.isSegmentOpen = false));
    },
    setSegmentActive(active) {
      const text = active
        ? 'Activate this segment? Its users are recomputed before its next due notification.'
        : 'Deactivate this segment? Its notification rules stop sending until it is activated again.';
      return this.confirmThen(active ? 'Activate' : 'Deactivate', text, () =>
        axios.patch(API + `segments/${this.selectedSegmentId}`, { active })
      );
    },
    deleteNotificationRule(rule) {
      return this.confirmThen('Delete', `Delete the notification rule "${rule.cron}" and its messages?`, () =>
        axios.delete(API + `notification-rules/${rule.id}`)
      );
    },
    deleteSegmentRule(rule) {
      return this.confirmThen('Delete', `Delete the rule ${rule.key}: ${rule.value}?`, () =>
        axios.delete(API + `rules/${rule.id}`)
      );
    },
    addNotificationRule() {
      this.notificationRuleId = null;
      this.notificationRuleCron = '';
      this.notificationRuleActive = false;
      this.notificationRuleTier = 'T3';
      this.notificationRulePriority = 0;
      this.notificationRuleCooldownDays = null;
      this.notificationRuleAllowQuietHours = false;
      this.notificationRuleSpecialDayKinds = [];
      this.dialogError = '';
      this.isNotificationRuleOpen = true;
    },
    editNotificationRule(rule) {
      this.notificationRuleId = rule.id;
      this.notificationRuleCron = rule.cron;
      this.notificationRuleActive = rule.active;
      this.notificationRuleTier = rule.tier;
      this.notificationRulePriority = rule.priority;
      this.notificationRuleCooldownDays = rule.cooldown_days;
      this.notificationRuleAllowQuietHours = rule.allow_quiet_hours;
      this.notificationRuleSpecialDayKinds = parseKinds(rule.special_day_kind);
      this.dialogError = '';
      this.isNotificationRuleOpen = true;
    },
    saveNotificationRule() {
      const settings = {
        tier: this.notificationRuleTier,
        priority: toInteger(this.notificationRulePriority) || 0,
        cooldown_days: toInteger(this.notificationRuleCooldownDays),
        allow_quiet_hours: this.notificationRuleAllowQuietHours,
        special_day_kind: this.notificationRuleTier === 'TC' ? this.notificationRuleSpecialDayKinds.join(',') : null,
      };
      const request =
        this.notificationRuleId === null
          ? () =>
              axios.post(API + `segments/${this.selectedSegmentId}/notification-rules`, {
                cron: this.notificationRuleCron,
                ...settings,
              })
          : () =>
              axios.patch(API + `notification-rules/${this.notificationRuleId}`, {
                cron: this.notificationRuleCron,
                active: this.notificationRuleActive,
                ...settings,
              });
      return this.submit(request, () => (this.isNotificationRuleOpen = false));
    },
    addSegmentRule() {
      this.segmentRuleId = null;
      this.segmentRuleKey = '';
      this.segmentRuleValue = '';
      this.segmentRuleNegate = false;
      this.dialogError = '';
      this.isSegmentRuleOpen = true;
    },
    editSegmentRule(rule) {
      this.segmentRuleId = rule.id;
      this.segmentRuleKey = rule.key;
      this.segmentRuleValue = rule.value;
      this.segmentRuleNegate = rule.negate;
      this.dialogError = '';
      this.isSegmentRuleOpen = true;
    },
    saveSegmentRule() {
      const payload = {
        key: this.segmentRuleKey,
        value: this.segmentRuleValue,
        negate: this.segmentRuleNegate,
      };
      const request =
        this.segmentRuleId === null
          ? () => axios.post(API + `segments/${this.selectedSegmentId}/rules`, payload)
          : () => axios.patch(API + `rules/${this.segmentRuleId}`, payload);
      return this.submit(request, () => (this.isSegmentRuleOpen = false));
    },
  },
  computed: {
    selectedSegment() {
      const e = this.segmentItems.find((item) => item.value === this.selectedSegmentId);
      if (e) {
        return e.data;
      }
      return null;
    },
    isSegmentDataAvailable() {
      return !!this.selectedSegment;
    },
    segmentRuleValueHint() {
      const known = SEGMENT_RULE_KEYS.find(({ key }) => key === this.segmentRuleKey);
      return known ? known.value : 'Comma-separated, e.g. 0,0';
    },
    notificationRuleCooldownHint() {
      return 'Days before the same user gets this rule again, 0 to 60. Empty uses the tier default (T1 once per 60 days, T2 14, T3 7, TC 0, T0 0). 0 means no cooldown.';
    },
    specialDayKindItems() {
      return this.specialDayKinds.map(({ kind, label }) => ({ value: kind, text: label.th }));
    },
    specialDayPreview() {
      const dates = nextSpecialDates(this.specialDayKinds, this.notificationRuleSpecialDayKinds);
      if (this.notificationRuleSpecialDayKinds.length === 0) {
        return 'เลือกอย่างน้อยหนึ่งประเภท จัดการวันได้ที่เมนู ปฏิทินวันพิเศษ';
      }
      return dates.length > 0
        ? `วันพิเศษถัดไป: ${dates.join(', ')} (ส่งเมื่อ cron ตรงกับวันนั้นด้วย)`
        : 'ยังไม่มีวันในปฏิทินของประเภทที่เลือก rule นี้จะยังไม่ส่ง';
    },
    segmentSize() {
      return new Intl.NumberFormat('th-TH').format(this.selectedSegment.size);
    },
  },
  mounted() {
    this.refresh();
  },
});
